import { Injectable } from '@angular/core';

import {
  GeneralTitleOption,
  GeneralTitleValue,
  ProfessionalTitleOption,
  ProfessionalTitleValue,
  SalutationFieldsConfig,
} from '@core/salutation-fields-types';

@Injectable()
export class SalutationFieldsHelperService {
  public static getProfessionalTitleOptions = (
    config: SalutationFieldsConfig,
    currentLanguage: string,
  ): ProfessionalTitleOption[] => {
    const options = config.translations.find(t => t.langCode === currentLanguage)?.titles;
    return SalutationFieldsHelperService.filterGenderForms(options ?? []);
  };

  public static getGeneralTitleOptions = (
    config: SalutationFieldsConfig,
    currentLanguage: string,
  ): GeneralTitleOption[] => {
    const options = config.translations.find(t => t.langCode === currentLanguage)?.salutations;
    return options ?? [];
  };

  public static interpolateCandidateGeneralTitle = ({
    salutationFieldsConfig,
    candidateGeneralTitle,
    candidatePlacementLanguage,
    offerLanguage,
    companyLanguage,
  }: {
    salutationFieldsConfig: SalutationFieldsConfig;
    candidateGeneralTitle: GeneralTitleValue | null;
    candidatePlacementLanguage: string | null;
    offerLanguage: string | null;
    companyLanguage: string;
  }): string | undefined => {
    const language = SalutationFieldsHelperService.selectTranslationLanguage({
      salutationFieldsConfig,
      candidatePlacementLanguage,
      offerLanguage,
      companyLanguage,
    });
    const options = SalutationFieldsHelperService.getSalutationOptions(
      salutationFieldsConfig,
      language,
    );
    const selectedOption = options.find(option => option.value === candidateGeneralTitle);

    if (!selectedOption || selectedOption.value === GeneralTitleValue.None) {
      return undefined;
    }

    return selectedOption.label;
  };

  public static interpolateCandidateProfessionalTitle = ({
    salutationFieldsConfig,
    candidateGeneralTitle,
    candidateProfessionalTitle,
    candidatePlacementLanguage,
    offerLanguage,
    companyLanguage,
  }: {
    salutationFieldsConfig: SalutationFieldsConfig;
    candidateGeneralTitle: GeneralTitleValue | null;
    candidateProfessionalTitle: ProfessionalTitleValue | null;
    candidatePlacementLanguage: string | null;
    offerLanguage: string | null;
    companyLanguage: string;
  }): string | undefined => {
    const language = SalutationFieldsHelperService.selectTranslationLanguage({
      salutationFieldsConfig,
      candidatePlacementLanguage,
      offerLanguage,
      companyLanguage,
    });
    const options = SalutationFieldsHelperService.getTitleOptions(salutationFieldsConfig, language);
    const isGeneralTitleDefined =
      SalutationFieldsHelperService.isSalutationDefined(candidateGeneralTitle);
    const isProfessionalTitleDefined = SalutationFieldsHelperService.isTitleDefined(
      candidateProfessionalTitle,
    );
    const combinedTitles = [
      ...(isGeneralTitleDefined && isProfessionalTitleDefined ? [candidateGeneralTitle] : []),
      ...(isProfessionalTitleDefined ? [candidateProfessionalTitle] : []),
    ].join('_');
    const selectedOption = options.find(option => option.value === combinedTitles);

    return selectedOption ? selectedOption.label : undefined;
  };

  public static interpolateFormalGreetingsTitlesLast = ({
    salutationFieldsConfig,
    candidateName,
    candidateGeneralTitle,
    candidateProfessionalTitle,
    candidatePlacementLanguage,
    offerLanguage,
    companyLanguage,
  }: {
    salutationFieldsConfig: SalutationFieldsConfig;
    candidateName: string;
    candidateGeneralTitle: GeneralTitleValue | null;
    candidateProfessionalTitle: ProfessionalTitleValue | null;
    candidatePlacementLanguage: string | null;
    offerLanguage: string | null;
    companyLanguage: string;
  }): string => {
    const nameWords = candidateName.split(/\s+/);
    const firstName = nameWords[0];
    const lastName = nameWords[nameWords.length - 1];
    const language = SalutationFieldsHelperService.selectTranslationLanguage({
      salutationFieldsConfig,
      candidatePlacementLanguage,
      offerLanguage,
      companyLanguage,
    });
    const options = SalutationFieldsHelperService.getGreetingsOptions(
      salutationFieldsConfig,
      language,
    );
    const isGeneralTitleDefined =
      SalutationFieldsHelperService.isSalutationDefined(candidateGeneralTitle);
    const isProfessionalTitleDefined = SalutationFieldsHelperService.isTitleDefined(
      candidateProfessionalTitle,
    );
    const combinedTitles =
      [
        ...(isGeneralTitleDefined ? [candidateGeneralTitle] : []),
        ...(isProfessionalTitleDefined ? [candidateProfessionalTitle] : []),
      ].join('_') || 'none';
    const selectedOption = options.find(option => option.value === combinedTitles)!;
    const isFiOrSvLanguage = language === 'fi' || language === 'sv';
    const nameLabel = isFiOrSvLanguage
      ? firstName
      : combinedTitles === 'none'
        ? candidateName
        : lastName;
    return selectedOption.label.replace('%{name}', nameLabel);
  };

  private static selectTranslationLanguage({
    salutationFieldsConfig,
    candidatePlacementLanguage,
    offerLanguage,
    companyLanguage,
  }: {
    salutationFieldsConfig: SalutationFieldsConfig;
    candidatePlacementLanguage: string | null;
    offerLanguage: string | null;
    companyLanguage: string;
  }): string {
    if (
      candidatePlacementLanguage &&
      SalutationFieldsHelperService.isLanguageAvailable(
        salutationFieldsConfig,
        candidatePlacementLanguage,
      )
    ) {
      return candidatePlacementLanguage;
    }

    if (
      offerLanguage &&
      SalutationFieldsHelperService.isLanguageAvailable(salutationFieldsConfig, offerLanguage)
    ) {
      return offerLanguage;
    }

    return SalutationFieldsHelperService.isLanguageAvailable(
      salutationFieldsConfig,
      companyLanguage,
    )
      ? companyLanguage
      : 'en';
  }

  private static isLanguageAvailable(
    salutationFieldsConfig: SalutationFieldsConfig,
    langCode: string,
  ): boolean {
    return salutationFieldsConfig.translations.some(t => t.langCode === langCode);
  }

  private static getSalutationOptions(
    salutationFieldsConfig: SalutationFieldsConfig,
    langCode: string,
  ) {
    return (
      salutationFieldsConfig.translations.find(t => t.langCode === langCode)?.salutations || []
    );
  }

  private static getTitleOptions(salutationFieldsConfig: SalutationFieldsConfig, langCode: string) {
    return salutationFieldsConfig.translations.find(t => t.langCode === langCode)?.titles || [];
  }

  private static getGreetingsOptions(
    salutationFieldsConfig: SalutationFieldsConfig,
    langCode: string,
  ) {
    return salutationFieldsConfig.translations.find(t => t.langCode === langCode)?.greetings || [];
  }

  private static filterGenderForms<T extends { value: string }>(options: T[]): T[] {
    return options.filter(option => !option.value.includes('_'));
  }

  private static isSalutationDefined(candidateGeneralTitle: GeneralTitleValue | null): boolean {
    return candidateGeneralTitle !== null && candidateGeneralTitle !== GeneralTitleValue.None;
  }

  private static isTitleDefined(
    candidateProfessionalTitle: ProfessionalTitleValue | null,
  ): boolean {
    return (
      candidateProfessionalTitle !== null &&
      candidateProfessionalTitle !== ProfessionalTitleValue.None
    );
  }
}
